import { useState } from 'react';

// Only show on the Readdy editor, not on the live site
const isEditMode = typeof window !== 'undefined' &&
  (window.location.hostname.includes('readdy.ai') ||
   window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1');

interface LightControlPanelProps {
  lightIntensity: number;
  onLightChange: (intensity: number) => void;
  currentWing: 'main' | 'north' | 'east' | 'south' | 'west' | null;
}

export default function LightControlPanel({ lightIntensity, onLightChange }: LightControlPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isEditMode) return null;

  const presets = [
    { name: 'Dim', value: 40, icon: 'ri-moon-line' },
    { name: 'Medium', value: 70, icon: 'ri-sun-line' },
    { name: 'Bright', value: 100, icon: 'ri-sun-fill' },
  ];

  return (
    <div className="fixed bottom-24 left-8 z-40">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-14 h-14 flex items-center justify-center bg-white border border-neutral-200 text-black hover:bg-neutral-50 transition-all duration-300 cursor-pointer"
      >
        <i className={`ri-lightbulb-line text-xl transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
      </button>

      {isExpanded && (
        <div className="absolute bottom-20 left-0 bg-white border border-neutral-200 p-6 w-80 animate-fadeIn">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-light text-black tracking-wide">Lighting</h3>
            <button
              onClick={() => setIsExpanded(false)}
              className="w-8 h-8 flex items-center justify-center text-neutral-500 hover:text-black transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Intensity Slider */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-base text-neutral-600 font-light">Brightness</span>
              <span className="text-base text-black">{lightIntensity}%</span>
            </div>
            <input
              type="range"
              min="20"
              max="100"
              value={lightIntensity}
              onChange={(e) => onLightChange(Number(e.target.value))}
              className="w-full h-2 bg-neutral-200 appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #171717 0%, #171717 ${lightIntensity}%, #e5e5e5 ${lightIntensity}%, #e5e5e5 100%)`
              }}
            />
          </div>

          {/* Presets */}
          <div className="mb-4">
            <span className="text-base text-neutral-600 font-light mb-3 block">Presets</span>
            <div className="grid grid-cols-3 gap-3">
              {presets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => onLightChange(preset.value)}
                  className={`px-3 py-3 text-sm font-light transition-all cursor-pointer whitespace-nowrap ${
                    lightIntensity === preset.value
                      ? 'bg-black text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  <i className={`${preset.icon} text-lg mb-1 block`}></i>
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="border border-neutral-200 p-4 bg-neutral-50">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-lg mt-0.5 text-neutral-600"></i>
              <p className="text-sm text-neutral-600 font-light leading-relaxed">
                Adjust the gallery lighting to view artworks in different conditions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}